/**
 * ResetPasswordPage — Réinitialisation du mot de passe via token
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSettings } from '../contexts/ThemeContext.js';
import { apiFetch } from '../utils/api.js';
import { colors } from '../styles/tokens.js';

export function ResetPasswordPage() {
  const settings = useSettings();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);

    try {
      const res = await apiFetch<{ message: string }>('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      });

      if (res.success) {
        setSuccess(true);
      } else {
        setError(res.error ?? 'Une erreur est survenue');
      }
    } catch {
      setError('Impossible de contacter le serveur');
    } finally {
      setIsLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-surface rounded-xl shadow-lg p-6 border border-gray-100 space-y-4">
            <i className="bi bi-exclamation-triangle" style={{ fontSize: 32, color: colors.danger }} />
            <h2 className="text-lg font-semibold text-accent">Lien invalide</h2>
            <p className="text-sm text-text-light">
              Ce lien de réinitialisation n'est pas valide. Faites une nouvelle demande.
            </p>
            <Link
              to="/forgot-password"
              className="inline-block mt-4 text-sm font-medium hover:underline"
              style={{ color: colors.accent }}
            >
              Mot de passe oublié
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md">
        {/* En-tête */}
        <div className="text-center mb-8">
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt={settings.siteName}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="h-16 w-16 mx-auto mb-4 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-2xl font-bold text-accent">A</span>
            </div>
          )}
          <h1 className="text-2xl font-heading font-bold text-accent">
            {settings.siteName}
          </h1>
        </div>

        <div className="bg-surface rounded-xl shadow-lg p-6 border border-gray-100">
          {success ? (
            /* Confirmation */
            <div className="text-center space-y-4">
              <div
                className="h-12 w-12 mx-auto rounded-full flex items-center justify-center"
                style={{ backgroundColor: '#dcfce7' }}
              >
                <i className="bi bi-check-lg" style={{ fontSize: 24, color: colors.success }} />
              </div>
              <h2 className="text-lg font-semibold text-accent">Mot de passe modifié</h2>
              <p className="text-sm text-text-light">
                Votre mot de passe a été mis à jour. Vous pouvez maintenant vous connecter.
              </p>
              <Link
                to="/login"
                className="inline-block mt-2 px-6 py-2.5 rounded-lg bg-primary text-accent font-semibold
                           hover:bg-primary-light transition-colors"
              >
                Se connecter
              </Link>
            </div>
          ) : (
            /* Formulaire */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <i className="bi bi-shield-lock" style={{ fontSize: 32, color: colors.accent, display: 'block', marginBottom: 8 }} />
                <h2 className="text-lg font-semibold text-accent">Nouveau mot de passe</h2>
                <p className="text-sm text-text-light mt-1">
                  Choisissez un nouveau mot de passe (minimum 8 caractères)
                </p>
              </div>

              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <i className="bi bi-lock absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: 14, color: colors.textMuted }} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    autoFocus
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300
                               text-text placeholder:text-text-light
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Minimum 8 caractères"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light hover:text-text transition-colors"
                    aria-label={showPassword ? 'Masquer' : 'Afficher'}
                  >
                    <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} style={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-text mb-1">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <i className="bi bi-lock-fill absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: 14, color: colors.textMuted }} />
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300
                               text-text placeholder:text-text-light
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    placeholder="Retapez le mot de passe"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 rounded-lg bg-primary text-accent font-semibold
                           hover:bg-primary-light transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Modification...' : 'Modifier le mot de passe'}
              </button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm hover:underline"
                  style={{ color: colors.accent }}
                >
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
